import React from 'react';
import { Link } from 'react-router-dom';
import { can, getAccess, isGroupOwnerHere, isSelfEmployedMe, useAccess } from './access';
import './permissions.css';

/**
 * Route guard for screens a role may not cover, so a typed URL or Cmd+K
 * can't open them (the nav already hides the entry). The server enforces
 * regardless — this is about not showing a screen that would only fail.
 *
 *   requireCapability(Finance, { any: ['reports'], selfEmployed: true })
 *
 * any:          passes with any of these capabilities
 * selfEmployed: also passes for a self-employed stylist (their own book)
 * owner:        passes only for the account holder / group owner
 * groupOwner:   passes only for the owner of the whole business (Locations,
 *               add a location — a linked site's account holder can't)
 *
 * Always applies (2026-10-02): it reads the server's user.access, which the
 * server enforces whatever the FE flag says. Someone nobody gave a role gets
 * their account type's default — exactly what they could do before roles.
 */
// Memoised per (component, rule): routes.js calls this inline in render, and a
// fresh component type per render would remount the page (see the note on
// ClientsGuarded in routes.js). A stable input stays a stable output.
const _cache = new WeakMap();

/**
 * Does `rule` let the signed-in person in? The owner always passes (bar
 * group-wide screens); `owner: true` alone means nobody else, `selfEmployed`
 * / `any` widen it. Pure apart from reading access — tested.
 */
export function ruleAllows(rule = {}, access = getAccess()) {
  if (rule.groupOwner) return isGroupOwnerHere();
  return !!access.owner || !!(rule.selfEmployed && isSelfEmployedMe()) || (rule.any || []).some((c) => can(c));
}

export default function requireCapability(Component, rule = {}) {
  const key = JSON.stringify(rule);
  let byRule = _cache.get(Component);
  if (!byRule) { byRule = new Map(); _cache.set(Component, byRule); }
  if (byRule.has(key)) return byRule.get(key);
  function Guarded(props) {
    const { access } = useAccess();
    const ok = ruleAllows(rule, access);
    if (ok) return <Component {...props} />;
    const salonId = localStorage.getItem('salonId');
    return (
      <div className="perm-noaccess">
        <div className="perm-noaccess-card">
          <h2>You don't have access to this</h2>
          <p>Your role doesn't include it. If you need it, ask the salon owner to change your role in Team, Permissions.</p>
          <Link className="bu-inline-save" to={salonId ? `/diary/${salonId}` : '/'}>Go to the diary</Link>
        </div>
      </div>
    );
  }
  Guarded.displayName = `requireCapability(${Component.displayName || Component.name || 'Component'})`;
  byRule.set(key, Guarded);
  return Guarded;
}
